import React, { useEffect } from 'react';
import AppFooter from '../components/AppFooter';

const PAGE_TITLE = 'Privacy Policy - Syncly';
const PAGE_DESCRIPTION =
  'Read how Syncly, a product of Aiolos, collects, uses, shares, and protects personal information.';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute('content');

    document.title = PAGE_TITLE;
    if (descriptionTag) {
      descriptionTag.setAttribute('content', PAGE_DESCRIPTION);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionTag && previousDescription) {
        descriptionTag.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="privacy-page">
      <main className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-14">
          <header className="border-b border-border pb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Legal</p>
            <h1 className="mt-2 text-4xl md:text-5xl tracking-tight uppercase text-foreground">
              Privacy Policy
            </h1>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
              Syncly is a product of Aiolos (
              <a
                href="https://aiolos.cloud"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                https://aiolos.cloud
              </a>
              ).
            </p>
            <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
              Last updated: May 6, 2026
            </p>
          </header>

          <article className="space-y-8 pt-8">
            <section id="introduction" aria-labelledby="introduction-heading">
              <h2 id="introduction-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                1. Introduction
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                This Privacy Policy explains how Syncly and Aiolos collect, use, and protect
                information when you use Syncly web apps, APIs, and related services. This policy
                applies to users invited to Syncly workspaces and administrators managing teams in
                Syncly.
              </p>
            </section>

            <section id="information-collect" aria-labelledby="information-collect-heading">
              <h2 id="information-collect-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                2. Information We Collect
              </h2>
              <ul className="mt-3 space-y-2 list-disc pl-6 text-sm md:text-base leading-7 text-foreground">
                <li>
                  Account details: first name, last name, email address, role, account status.
                </li>
                <li>
                  Authentication and security data: hashed password, password reset OTP data,
                  invitation token data, login timestamp, and authentication token/session metadata.
                </li>
                <li>
                  Workspace content: tasks, assignees, task descriptions, comments, subtasks,
                  progress updates, weekly notes/blockers, and timesheet entries.
                </li>
                <li>
                  Notification preferences and logs: email reminder settings and notification log
                  metadata (recipient, subject, send status, attempts, errors, timestamps).
                </li>
                <li>
                  Technical and operational data: request and server logs used for security,
                  troubleshooting, and service operations.
                </li>
                <li>
                  Browser-stored settings: Syncly stores authentication token and theme preference
                  in browser local storage.
                </li>
              </ul>
            </section>

            <section id="how-we-use" aria-labelledby="how-we-use-heading">
              <h2 id="how-we-use-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                3. How We Use Information
              </h2>
              <ul className="mt-3 space-y-2 list-disc pl-6 text-sm md:text-base leading-7 text-foreground">
                <li>To create and manage accounts, authenticate users, and maintain access control.</li>
                <li>To provide task management, collaboration, timesheet, and weekly reporting features.</li>
                <li>To send operational emails such as invitations, OTP/password reset, and reminders.</li>
                <li>To generate dashboards, progress views, and team-level operational insights.</li>
                <li>To maintain platform security, detect abuse, and troubleshoot service issues.</li>
                <li>To meet legal, regulatory, and contractual obligations.</li>
              </ul>
            </section>

            <section id="sharing-third-parties" aria-labelledby="sharing-third-parties-heading">
              <h2
                id="sharing-third-parties-heading"
                className="text-2xl md:text-3xl uppercase tracking-wide"
              >
                4. Data Sharing &amp; Third Parties
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Syncly does not sell personal information. Data may be shared only as needed to run
                the service and comply with law.
              </p>
              <ul className="mt-3 space-y-2 list-disc pl-6 text-sm md:text-base leading-7 text-foreground">
                <li>
                  Workspace visibility: task content, comments, assignments, and progress can be
                  visible to authorized members and admins in the same Syncly workspace.
                </li>
                <li>
                  Email delivery: Syncly uses SMTP-based email delivery providers for invitations,
                  OTP, and reminder notifications.
                </li>
                <li>
                  Infrastructure/data hosting: Syncly stores service data in MongoDB-backed
                  infrastructure managed by Aiolos and hosting providers acting on Aiolos&apos; instructions.
                </li>
                <li>
                  Third-party web content: the web app loads Google Fonts and a login-page image
                  from Unsplash, which may receive standard request metadata (for example, IP
                  address and user agent) when those resources are loaded.
                </li>
                <li>
                  Legal requests: data may be disclosed when required by law, court order, or to
                  protect legal rights and platform security.
                </li>
              </ul>
            </section>

            <section id="storage-security" aria-labelledby="storage-security-heading">
              <h2 id="storage-security-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                5. Data Storage &amp; Security
              </h2>
              <ul className="mt-3 space-y-2 list-disc pl-6 text-sm md:text-base leading-7 text-foreground">
                <li>Passwords are stored as bcrypt hashes (not plaintext).</li>
                <li>Authentication is token-based using signed JWTs.</li>
                <li>Security controls include API authentication, role-based authorization, CORS policy, and security headers.</li>
                <li>
                  Data is retained while accounts and workspaces are active and as needed for
                  operational, legal, and security purposes.
                </li>
                <li>
                  Where records are soft-deleted in Syncly, they may remain in secured systems for
                  audit, recovery, or legal obligations.
                </li>
              </ul>
            </section>

            <section id="user-rights" aria-labelledby="user-rights-heading">
              <h2 id="user-rights-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                6. User Rights
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Depending on your location and applicable law, you may have rights to request access,
                correction, deletion, or restriction of processing of your personal data. You may also
                request account deactivation. Syncly admins can update workspace data directly, and
                additional privacy requests can be submitted to Aiolos through the contact details
                below.
              </p>
            </section>

            <section id="cookies-tracking" aria-labelledby="cookies-tracking-heading">
              <h2 id="cookies-tracking-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                7. Cookies / Tracking
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Syncly currently does not use first-party advertising cookies or in-app behavioral
                advertising trackers. The web app uses local storage for authentication token and theme
                preference. Third-party resources (such as Google Fonts and Unsplash image assets) may
                process standard web request metadata when loaded.
              </p>
            </section>

            <section id="childrens-privacy" aria-labelledby="childrens-privacy-heading">
              <h2 id="childrens-privacy-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                8. Children&apos;s Privacy
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Syncly is intended for workplace and professional use and is not directed to children
                under 13 years of age. If Aiolos learns that personal data of a child was provided
                without appropriate authorization, we will take steps to delete that information.
              </p>
            </section>

            <section id="changes" aria-labelledby="changes-heading">
              <h2 id="changes-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                9. Changes to Policy
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Aiolos may update this Privacy Policy from time to time to reflect product changes,
                legal requirements, or operational updates. The updated version will be posted on this
                page with a revised &quot;Last updated&quot; date.
              </p>
            </section>

            <section id="contact" aria-labelledby="contact-heading">
              <h2 id="contact-heading" className="text-2xl md:text-3xl uppercase tracking-wide">
                10. Contact Information
              </h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground">
                Syncly is operated by Aiolos.
              </p>
              <ul className="mt-3 space-y-2 list-disc pl-6 text-sm md:text-base leading-7 text-foreground">
                <li>
                  Company: Aiolos
                </li>
                <li>
                  Website:{' '}
                  <a
                    href="https://aiolos.cloud"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://aiolos.cloud
                  </a>
                </li>
                <li>
                  Privacy requests: submit through Aiolos contact channels and reference
                  &quot;Syncly Privacy Request.&quot;
                </li>
              </ul>
            </section>
          </article>

        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
